import { Box, InputAdornment, ListItemIcon, SvgIcon, Typography } from "@mui/material";
import { MessageError, MOBILE_MEDIA_DEVICE_ID } from "@yujacorp/verity-api-contract";
import { CoreMenuItem, CoreProgressBar, CoreSelectTextField, CoreSpinner } from "../../core";
import { useFakeProgress } from "../../progressBar";
import styles from "./index.module.css";
import i18nPackage from "../../i18n/i18n";
import React, { ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { SubstepStatus } from "./basic-substep";
import FailedIcon from "../../images/exclamation-filled.svg?react";
import SuccessIcon from "../../images/system-check-check.svg?react";
import WebcamIcon from "../../images/webcam.svg?react";
import MicrophoneIcon from "../../images/microphone.svg?react";
import PhoneIcon from "../../images/phone.svg?react";

export type DeviceErrorType = "noPermission" | "notFound" | "deviceInUse" | "unknown";

export type AudioErrorType = DeviceErrorType | "noAudio";

export type DeviceDropdownProps = {
	allowMobile?: boolean;
	devices: Omit<MediaDeviceInfo, "toJSON">[];
	type: "audio" | "camera";
	status?: SubstepStatus;
	selectedDeviceId: string | undefined;
	onChange: (deviceId?: string) => void;
	container?: HTMLElement | null;
	disabled?: boolean;
	label?: ReactNode;
};

function DeviceLabelIcon({ children }: { children: ReactNode }) {
	return <SvgIcon className={styles.deviceLabelIcon}>{children}</SvgIcon>;
}

export function DeviceDropdown({
	devices,
	type,
	allowMobile = false,
	status = "unstarted",
	selectedDeviceId,
	onChange,
	container,
	disabled,
	label,
}: DeviceDropdownProps) {
	const dropdownText =
		type === "audio"
			? i18nPackage.t("beforeQuiz.deviceSetup.audioRecordingMode.microphoneNotFound")
			: devices.length === 0
				? i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.webcamNotFound")
				: i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.chooseWebcam");

	const deviceLabel =
		label ??
		(type === "audio"
			? i18nPackage.t("beforeQuiz.deviceSetup.audioRecordingMode.chooseMicrophone")
			: i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.chooseWebcam"));

	const displayedDevices: Omit<MediaDeviceInfo, "toJSON">[] = [...devices];
	if (allowMobile) {
		displayedDevices.push({
			deviceId: MOBILE_MEDIA_DEVICE_ID,
			groupId: MOBILE_MEDIA_DEVICE_ID,
			kind: type === "audio" ? "audioinput" : "videoinput",
			label: i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.mobileCamera"),
		});
	}

	const defaultSourceIcon = type === "audio" ? <MicrophoneIcon /> : <WebcamIcon />;

	const labelId = useId();

	return (
		<div className={styles.deviceContainer}>
			<Typography className={styles.deviceLabel} id={labelId}>
				{status === "failed" && (
					<DeviceLabelIcon>
						<FailedIcon role="img" aria-label={i18nPackage.t("beforeQuiz.substepFailed")} />
					</DeviceLabelIcon>
				)}
				{status === "completed" && (
					<DeviceLabelIcon>
						<SuccessIcon role="img" aria-label={i18nPackage.t("beforeQuiz.substepCompleted")} />
					</DeviceLabelIcon>
				)}
				{deviceLabel}
			</Typography>
			<CoreSelectTextField<string>
				fullWidth
				ariaLabelledBy={labelId}
				onChange={(e) => onChange(e.target.value)}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<SvgIcon className={styles.deviceDropdownIcon}>
								{selectedDeviceId === MOBILE_MEDIA_DEVICE_ID ? <PhoneIcon /> : defaultSourceIcon}
							</SvgIcon>
						</InputAdornment>
					),
				}}
				label={dropdownText}
				value={selectedDeviceId ?? ""}
				renderValue={(selected) => {
					if (!selected) return <span className={styles.placeholder}>{dropdownText}</span>;
					const renderedLabel = displayedDevices.find((o) => o.deviceId === selected)?.label;
					return <span>{renderedLabel ?? dropdownText}</span>;
				}}
				SelectProps={{
					className: styles.deviceDropdown,
					MenuProps: {
						slotProps: {
							paper: {
								sx: {
									maxHeight: "105px",
								},
							},
						},
					},
				}}
				shadowDomContainer={container}
				size="small"
				disabled={disabled}
			>
				{displayedDevices.map((option) => {
					return (
						<CoreMenuItem className={styles.deviceDropdownEntry} key={option.deviceId} value={option.deviceId}>
							<ListItemIcon>
								<SvgIcon className={styles.deviceDropdownIcon}>
									{option.deviceId === MOBILE_MEDIA_DEVICE_ID ? <PhoneIcon /> : defaultSourceIcon}
								</SvgIcon>
							</ListItemIcon>
							{option.label}
						</CoreMenuItem>
					);
				})}
			</CoreSelectTextField>
		</div>
	);
}

export type CameraPreviewProps = {
	/** The canvas element to render the camera feed into */
	canvasRef: React.Ref<HTMLCanvasElement>;
	cameraError?: DeviceErrorType;
	canvasClassName?: string;
	canvasContainerClassName?: string;
	isVideoLoading?: boolean;
};

export function CameraPreview({
	canvasRef,
	cameraError,
	canvasClassName,
	canvasContainerClassName,
	isVideoLoading,
}: CameraPreviewProps) {
	const cameraErrorKey: DeviceErrorType = cameraError ?? "unknown";

	return (
		<div
			className={`${styles.cameraPreviewContainer} ${canvasContainerClassName ? canvasContainerClassName : ""} ${cameraError ? styles.cameraError : ""}`}
		>
			{cameraError && (
				<Typography>
					{i18nPackage.t(`beforeQuiz.deviceSetup.videoRecordingMode.title.${cameraErrorKey}.single`)}
				</Typography>
			)}
			{isVideoLoading && !cameraError && (
				<Box width={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"} height={"100%"}>
					<CoreSpinner size={40} />
				</Box>
			)}
			{/**Always mount canvas to prevent race conditions */}
			<canvas
				ref={canvasRef}
				className={`${styles.videoCanvas} ${canvasClassName ? canvasClassName : ""}`}
				style={{ display: isVideoLoading || cameraError ? "none" : "" }}
			/>
		</div>
	);
}

export function getCameraDescriptionError(cameraError: DeviceErrorType) {
	return i18nPackage.t(`beforeQuiz.deviceSetup.videoRecordingMode.description.${cameraError}.single`);
}

export function getCameraTitleError(cameraError: DeviceErrorType) {
	return i18nPackage.t(`beforeQuiz.deviceSetup.videoRecordingMode.title.${cameraError}.single`);
}

export type WaveformProps = {
	volume: number;
	height?: number;
};

export function Waveform({ volume, height }: WaveformProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [canvasWidth, setCanvasWidth] = useState<number>(300);

	// Observe parent width
	useEffect(() => {
		if (!containerRef.current) return;
		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setCanvasWidth(entry.contentRect.width);
			}
		});
		resizeObserver.observe(containerRef.current);
		return () => resizeObserver.disconnect();
	}, []);

	const canvasHeight = height ?? 15; // Canvas height
	const segments = 25; // Number of segments in the bar
	const padding = 2; // Padding between segments
	const radius = 6; // Radius of the first and last chunk

	const buffer = useMemo(() => [0, 0, 0, 0, 0], []);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		// Smooth the volume readings
		buffer.shift();
		buffer.push(volume);

		ctx.clearRect(0, 0, canvasWidth, canvasHeight);

		// Normalize volume (-80 dB to 0 dB → 0–1)
		const normalizedValue = (volume + 80) / 80;

		const segmentWidth = (canvasWidth - padding * (segments - 1)) / segments;
		const activeSegments = Math.ceil(normalizedValue * segments);

		for (let i = 0; i < segments; i++) {
			const x = i * (segmentWidth + padding);
			const isActive = i < activeSegments;

			const gradient = ctx.createLinearGradient(x, 0, x + segmentWidth, 0);
			gradient.addColorStop(1, isActive ? (volumeColorMap[i.toString()] ?? "#4caf50") : "#EFEFEF");

			ctx.fillStyle = gradient;
			ctx.beginPath();

			if (i === 0) {
				// Leftmost segment (rounded left edges)
				ctx.roundRect(x, 0, segmentWidth, canvasHeight, [radius, 0, 0, radius]);
			} else if (i === segments - 1) {
				// Rightmost segment (rounded right edges)
				ctx.roundRect(x, 0, segmentWidth, canvasHeight, [0, radius, radius, 0]);
			} else {
				// Middle segments (square)
				ctx.rect(x, 0, segmentWidth, canvasHeight);
			}

			ctx.fill();
		}
	}, [buffer, canvasHeight, canvasWidth, volume]);

	return (
		<div ref={containerRef} className={styles.waveformContainer}>
			<canvas ref={canvasRef} width={canvasWidth} height={canvasHeight} />
		</div>
	);
}

// TO DO REMOVE EXTRA AND INVERT??
const volumeColorMap: Record<string, string> = {
	"0": "#cce5d6",
	"1": "#b2d8c1",
	"2": "#99cbad",
	"3": "#7fbe99",
	"4": "#66b184",
	"5": "#4ca470",
	"6": "#32975b",
	"7": "#198a47",
	"8": "#007e33",
	"9": "#00712d",
	"10": "#006428",
	"11": "#ffd9b3",
	"12": "#ffcd9a",
	"13": "#ffc181",
	"14": "#ffb468",
	"15": "#ffa84f",
	"16": "#ff9b36",
	"17": "#ff8f1d",
	"18": "#ff8304",
	"19": "#e57503",
	"20": "#cc6803",
	"21": "#ffb2b2",
	"22": "#ff9999",
	"23": "#ff7f7f",
	"24": "#ff6666",
	"25": "#ff4c4c",
	"26": "#ff3232",
	"27": "#ff1919",
	"28": "#ff0000",
	"29": "#e50000",
};

export function getDeviceErrorType(error: unknown): DeviceErrorType {
	let name: string | undefined;
	if (error instanceof DOMException) {
		name = error.name;
	} else if (error instanceof MessageError && error.errorClass === "DOMException") {
		name = error.name;
	} else if (error instanceof Error) {
		// SendMessageFunctionError
		const maybeWrapped = error as { error?: unknown };

		if (typeof maybeWrapped.error === "object" && maybeWrapped.error !== null) {
			const inner = maybeWrapped.error as { name?: unknown };

			if (typeof inner.name === "string") {
				name = inner.name;
			}
		}
	}

	switch (name) {
		case "NotAllowedError":
			return "noPermission";
		case "NotFoundError":
			return "notFound";
		case "NotReadableError":
			return "deviceInUse";
		default:
			return "unknown";
	}
}

export function BeforeQuizProgressBar({ error, className }: { error: boolean; className?: string }) {
	const progress = useFakeProgress(error);
	return (
		<div className={`${styles.progressContainer} ${className ? className : ""}`}>
			<CoreProgressBar
				variant="determinate"
				value={progress * 100}
				height={10}
				color={"#42296E"}
				backgroundColor={"#EDE8F8"}
				width={"100%"}
			/>
			<Typography>{`${Math.round(progress * 100)}%`}</Typography>
		</div>
	);
}
